import NewRoleDialog from "@src/features/roles/dialogs/NewRoleDialog";
import { HrisTeamFragment } from "@src/features/settings/hris/HrisRoleListContainer.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import useRoles from "@src/features/roles/hooks/useRoles";
import { GraphqlOperations } from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { Button } from "@src/lib/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Label } from "@src/lib/ui/Label";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import IdBadge2OutlineIcon from "@src/lib/icons/18px/id-badge-2-outline";
import PlusIcon from "@src/lib/icons/18px/plus";
import Fuse from "fuse.js";
import { FC, useMemo, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { useLinkHrisTeamToOpusRoleMutation } from "./LinkHrisTeamDialog.generated";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

const fuseConfig = {
  threshold: 0.4,
  keys: ["name"],
};

const CREATE_NEW_VALUE = "__create_new__";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hrisTeam: HrisTeamFragment;
};

const LinkHrisTeamDialog: FC<Props> = ({ open, onOpenChange, hrisTeam }) => {
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const [search, setSearch] = useState("");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const { roles } = useRoles();
  const [selectedValue, setSelectedValue] = useState<string>(
    hrisTeam.group?.id.toString() || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const initialValueRef = useRef<string>(hrisTeam.group?.id.toString() || "");

  const hasChanged = useMemo(() => {
    return selectedValue !== initialValueRef.current;
  }, [selectedValue]);

  const filteredRoles = useMemo(() => {
    if (search) {
      return new Fuse(roles, fuseConfig).search(search).map((e) => e.item);
    }
    return [...roles].sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }, [roles, search]);

  const selectedRole = useMemo(() => {
    if (selectedValue === CREATE_NEW_VALUE) {
      return {
        id: CREATE_NEW_VALUE,
        name: `Create new Opus role for ${hrisTeam.name}`,
      };
    }
    if (!selectedValue) return null;
    return (
      roles.find((role) => role.id.toString() === selectedValue) ||
      (hrisTeam.group?.id.toString() === selectedValue
        ? {
            id: hrisTeam.group.id,
            name: hrisTeam.group.name,
          }
        : null)
    );
  }, [selectedValue, roles, hrisTeam.group, hrisTeam.name]);

  const [createHrisTeamLink] = useLinkHrisTeamToOpusRoleMutation({
    refetchQueries: [
      GraphqlOperations.Query.HrisTeams,
      GraphqlOperations.Query.HrisRoleSidebar,
    ],
    onCompleted: (data) => {
      setIsSaving(false);
      if (!data.createHrisTeamLink.success) {
        addErrorToast({
          hrisTeamId: hrisTeam.id,
          opusRoleId:
            selectedValue !== CREATE_NEW_VALUE
              ? parseInt(selectedValue)
              : undefined,
          callsite: "new_hris_team_link",
        });
      } else {
        trackEvent("new_hris_team_link", {
          hrisTeamId: hrisTeam.id,
          opusRoleId:
            selectedValue !== CREATE_NEW_VALUE
              ? parseInt(selectedValue)
              : undefined,
        });
        onOpenChange(false);
        launchConfetti();
      }
    },
    onError: (error) => {
      setIsSaving(false);
      addErrorToast({
        ...error,
        callsite: "new_role_dialog",
      });
    },
  });

  const handleSave = () => {
    if (!selectedValue) return;

    setIsSaving(true);
    createHrisTeamLink({
      variables: {
        hrisTeamId: hrisTeam.id,
        groupId: parseInt(selectedValue),
      },
    });
  };

  const handleSelect = (value: string) => {
    if (value === CREATE_NEW_VALUE) {
      setComboboxOpen(false);
      setSearch("");
      setRoleDialogOpen(true);
    } else {
      setSelectedValue(value);
      setComboboxOpen(false);
      setSearch("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit opus role link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Opus role</Label>
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="h-9 w-full justify-between font-normal"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {(selectedRole || (selectedValue && hrisTeam.group)) &&
                    selectedRole?.id !== CREATE_NEW_VALUE && (
                      <IdBadge2OutlineIcon className="size-4 shrink-0 text-muted-foreground" />
                    )}
                  <span className="truncate">
                    {selectedRole
                      ? selectedRole.name
                      : selectedValue && hrisTeam.group?.name
                        ? hrisTeam.group.name
                        : "Select a role..."}
                  </span>
                </div>
                <ChevronDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
              disablePortal
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search for a role..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>
                    {search ? "No roles found" : "No roles available"}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value={CREATE_NEW_VALUE}
                      onSelect={handleSelect}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-1 items-center gap-2">
                        <PlusIcon
                          className={cn(
                            "h-4 w-4 shrink-0",
                            selectedValue === CREATE_NEW_VALUE
                              ? "text-primary"
                              : "text-muted-foreground",
                          )}
                        />
                        <Text
                          variant="compact-description"
                          className={cn(
                            selectedValue === CREATE_NEW_VALUE &&
                              "text-primary",
                          )}
                        >
                          Create new Opus role for {hrisTeam.name}
                        </Text>
                      </div>
                    </CommandItem>

                    {filteredRoles.map((role) => {
                      const value = role.id.toString();
                      const isSelected = selectedValue === value;
                      return (
                        <CommandItem
                          key={role.id}
                          value={value}
                          onSelect={handleSelect}
                          className="cursor-pointer"
                        >
                          <div className="flex flex-1 items-center gap-2">
                            <IdBadge2OutlineIcon
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected
                                  ? "text-primary"
                                  : "text-muted-foreground",
                              )}
                            />
                            <Text
                              variant="compact-description"
                              className={cn(isSelected && "text-primary")}
                            >
                              {role.name}
                            </Text>
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedValue || isSaving || !hasChanged}
            loading={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {roleDialogOpen && (
        <NewRoleDialog
          open={roleDialogOpen}
          onOpenChange={setRoleDialogOpen}
          initialInput={{ name: hrisTeam.name }}
          navigateToRoleOnSave={false}
          onRoleCreated={(roleId) => {
            setSelectedValue(roleId.toString());
          }}
        />
      )}
    </Dialog>
  );
};

export default LinkHrisTeamDialog;

gql`
  mutation LinkHrisTeamToOpusRole($groupId: Int!, $hrisTeamId: Int!) {
    createHrisTeamLink(groupId: $groupId, hrisTeamId: $hrisTeamId) {
      success
    }
  }
`;
